"use client";

import Image from "next/image";
import Link from "next/link";
import {
  useParams,
  usePathname,
  useRouter,
  useSelectedLayoutSegment,
} from "next/navigation";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import { BottomNavBarConditionalWrapper } from "~/components/BottomNavBar/BottomNavBar";
import { Box } from "~/components/Box/Box";
import { DesktopCheckoutButton } from "~/components/DesktopCheckoutButton/DesktopCheckoutButton";
import { HeaderAccountStatus } from "~/components/HeaderAccountStatus/HeaderAccountStatus";
import { Hidden } from "~/components/Hidden/Hidden";
import { Inline } from "~/components/Inline/Inline";
import { LatestOrderBanner } from "~/components/LatestOrderBanner";
import { OrderingOptionsButton } from "~/components/OrderingOptionsButton/OrderingOptionsButton";
import { PriorityMessageDrawer } from "~/components/PriorityMessageDrawer/PriorityMessageDrawer";
import { useCart } from "~/hooks/cart";
import { LocationProvider, useLocation } from "~/hooks/locations";
import { MenuProvider } from "~/hooks/menu-context";
import { OrderingConfigProvider } from "~/hooks/ordering";
import { useUser } from "~/hooks/user";
import type { Page } from "~/types";
import { appendSearchParams } from "~/utils/url";
import styles from "./Header.module.css";

export default function ClientLocationLayout(props: {
  children: React.ReactNode;
}) {
  const { location: locationRef } = useParams() as { location: string };
  const router = useRouter();
  const segment = useSelectedLayoutSegment() as Page | null;
  const pathName = usePathname();

  const page = pathName.includes("promo-code") ? "promo-code" : segment;

  const goToCart = () =>
    router.push(`/${locationRef}/cart`, {
      scroll: false,
    });

  const goToLocation = () =>
    router.push(`/${locationRef}`, {
      scroll: false,
    });

  return (
    <LocationProvider locationRef={locationRef}>
      <OrderingConfigProvider locationRef={locationRef}>
        <MenuProvider locationRef={locationRef}>
          <header style={{ position: "sticky", top: 0, zIndex: 2 }}>
            {/* pinned priority messages are portalled in here, so they stick
                with the header rather than fighting it for the top */}
            <div id="pinned-message-slot" />
            <Header
              locationRef={locationRef}
              page={page}
              goToCart={goToCart}
              goToLocation={goToLocation}
            />
          </header>
          {props.children}
          <PriorityMessageDrawer locationRef={locationRef} />
          <Hidden above="tablet">
            <BottomNavBarConditionalWrapper
              locationRef={locationRef}
              activeItem={page ?? undefined}
            />
          </Hidden>
        </MenuProvider>
      </OrderingConfigProvider>
    </LocationProvider>
  );
}

function Header({
  goToLocation,
  goToCart,
  locationRef,
  page,
}: {
  locationRef: string;
  goToCart: () => void;
  goToLocation: () => void;
  page: Page | null;
}) {
  const { cart } = useCart(locationRef);
  const { user, isLoading: userIsLoading, isGuest } = useUser();
  const { location, loading: locationLoading } = useLocation();

  const authenticated = Boolean(user && !isGuest);

  if (locationLoading || !location) return null;

  return (
    <>
      <LatestOrderBanner location={locationRef} />
      <Hidden below="desktop">
        <HeaderBox>
          <Box
            display={"flex"}
            width={"full"}
            flexDirection={"row"}
            alignItems="center"
            justifyContent={"spaceBetween"}
            minWidth={0}
            paddingLeft="5"
            gap="2"
          >
            <OrderingOptionsButton
              page={page}
              location={location}
              onClickDetails={() => goToLocation()}
            />
            <NavBox
              page={page}
              locationRef={locationRef}
              authenticated={authenticated}
              userIsLoading={userIsLoading}
            />
            {authenticated || userIsLoading ? (
              <Box paddingRight="4">
                <HeaderAccountStatus
                  locationRef={locationRef}
                  authenticated={authenticated}
                  firstName={user?.firstName}
                  isLoading={userIsLoading}
                />
              </Box>
            ) : null}
            <DesktopCheckoutButton
              itemCount={cart.items.reduce(
                (acc, item) => acc + item.quantity,
                0,
              )}
              onGoToCheckout={goToCart}
            />
          </Box>
        </HeaderBox>
      </Hidden>
      <Hidden above="tablet">
        <HeaderBox>
          <Box
            display={"flex"}
            width={"full"}
            flexDirection={"row"}
            alignItems="center"
            justifyContent={"spaceBetween"}
            minWidth={0}
            gap={"2"}
          >
            <OrderingOptionsButton
              page={page}
              location={location}
              onClickDetails={() => goToLocation()}
            />
            <HeaderAccountStatus
              locationRef={locationRef}
              authenticated={authenticated}
              firstName={user?.firstName}
              isLoading={userIsLoading}
            />
          </Box>
        </HeaderBox>
      </Hidden>
    </>
  );
}

function NavBox({
  page,
  locationRef,
  authenticated,
  userIsLoading,
}: {
  page: Page | null;
  locationRef: string;
  authenticated: boolean;
  userIsLoading: boolean;
}) {
  return (
    <Box
      flexGrow={1}
      display={"flex"}
      paddingLeft={"2"}
      paddingRight={"4"}
      flexDirection={"rowReverse"}
    >
      {/* The header is a fixed 75px tall, so the nav must never wrap onto a
          second line — it stays on one line and the location name truncates
          instead. */}
      <Inline space={"4"} alignY="center" wrap={false}>
        <NavBoxLink active={page === "menu"} href={`/${locationRef}/menu`}>
          MENU
        </NavBoxLink>
        <NavBoxLink
          active={page === "loyalty"}
          href={`/${locationRef}/loyalty`}
        >
          LOYALTY
        </NavBoxLink>
        <NavBoxLink
          active={page === "promo-code"}
          href={`/${locationRef}/account/promo-code`}
        >
          PROMO CODE
        </NavBoxLink>
        {userIsLoading ? (
          <Skeleton width={68} className={styles.headerLink} />
        ) : (
          <NavBoxLink
            active={["account", "activity"].includes(page || "")}
            href={
              authenticated
                ? `/${locationRef}/account`
                : appendSearchParams(`/login/rewards`, {
                    next: `/${locationRef}/menu`,
                    location: locationRef,
                  })
            }
          >
            {authenticated ? "ACCOUNT" : "LOGIN"}
          </NavBoxLink>
        )}
      </Inline>
    </Box>
  );
}

function NavBoxLink({
  href,
  active,
  children,
}: {
  href: string;
  active: boolean;
  children: React.ReactNode;
}) {
  return (
    <Link
      className={active ? styles.headerLinkActive : styles.headerLink}
      href={href}
      prefetch={false}
    >
      {children}
    </Link>
  );
}

function HeaderBox(props: { children: React.ReactNode }) {
  return (
    <Box
      display={"flex"}
      className="stickyHeader" // Used by SearchCategoryBar for the sticky top and drop shadow IntersectObserver
      style={{
        height: 75,
        backgroundColor: "var(--color-jets-black)",
      }}
      alignItems={"center"}
      justifyContent="center"
      paddingX={["4", "4", "5"]}
    >
      <Box display="flex" flexGrow={1} minWidth={0} style={{ maxWidth: 1500 }}>
        <Hidden below="tablet">
          <Link
            href="/"
            style={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              paddingRight: "8px",
            }}
          >
            <Image
              alt="Jet's Pizza"
              src="/images/jetspizzalogo_desktop.png"
              height={42}
              width={280}
              className={"largeLogo"}
            />
            <Image
              alt="Jet's Pizza"
              src="/images/jetspizzalogo_mobile.png"
              width={72}
              height={44}
              className={"smallLogo"}
            />
          </Link>
        </Hidden>
        <div style={{ flex: 1, minWidth: 0, justifyContent: "center" }}>
          <SkeletonTheme
            baseColor="var(--color-neutral-700)"
            highlightColor="var(--color-neutral-600)"
          >
            {props.children}
          </SkeletonTheme>
        </div>
      </Box>
    </Box>
  );
}
